/* Consent-gated, safe search -> click -> confirmed cart-add attribution. */
(() => {
  'use strict';
  const P = window.ReelCuteSearchPolicy;
  if (!P || window.__rcSearchAnalyticsLoaded) return;
  window.__rcSearchAnalyticsLoaded = true;
  const key = 'rc_search_attribution_v1';
  const ttl = 30 * 60 * 1000;
  const contexts = new WeakMap();
  let googleStarted = false, clarityStarted = false, lastConsent = false;
  const permitted = () => !P.preview && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  const wipe = () => { try { sessionStorage.removeItem(key); } catch (_) {} };
  function read() {
    if (!permitted()) return null;
    try {
      const state = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (!state || Date.now() - state.timestamp > ttl || Date.now() < state.timestamp ||
          P.inspect(state.search_term).status !== 'safe') { wipe(); return null; }
      return state;
    } catch (_) { return null; }
  }
  function write(state) {
    if (!permitted() || !state || P.inspect(state.search_term).status !== 'safe') return;
    try { sessionStorage.setItem(key, JSON.stringify(state)); } catch (_) {}
  }
  const freshId = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  function payload(query, surface, count, scope) {
    const safe = P.inspect(query);
    return {
      search_id: freshId(), search_term: safe.term || '(redacted)', query_normalized: safe.normalized || '(redacted)',
      query_status: safe.status, role_group: safe.role, search_surface: surface,
      result_count: Number(count) || 0, count_scope: scope, telemetry_version: P.version
    };
  }
  function send(name, params) {
    if (!permitted() || !googleStarted) return false;
    window.gtag('event', name, {
      ...params, page_location: P.safeUrl(location.href), page_referrer: P.safeUrl(document.referrer),
      page_title: /\/search$/.test(location.pathname) ? 'Search results | ReelCute' : document.title,
      transport_type: 'beacon'
    });
    if (clarityStarted) window.clarity('event', name);
    return true;
  }
  function collect(root, query, surface, count, scope) {
    if (!permitted() || !root) return null;
    const cards = [...root.querySelectorAll('[data-rc-search-product]')];
    const previous = contexts.get(root);
    const signature = `${P.inspect(query).term}|${P.inspect(query).status}|${count}|${surface}|${cards.map(c => c.dataset.rcSearchProduct + ':' + c.dataset.rcSearchPosition).join(',')}`;
    if (previous?.signature === signature) return previous.params;
    const params = payload(query, surface, count, scope);
    params.shown_product_count = cards.length;
    params.shown_reel_count = cards.filter(card => /(^| )badge reel$/i.test(card.dataset.rcSearchProductType || '')).length;
    params.shown_buddy_count = cards.filter(card => card.dataset.rcSearchProductType === 'Badge Buddy').length;
    params.result_page = surface === 'results' ? Math.max(1, Number(new URL(location.href).searchParams.get('page')) || 1) : 1;
    if (!send('rc_search_results', params)) return null;
    contexts.set(root, { signature, params });
    return params;
  }
  function collectPage() {
    const root = document.querySelector('[data-rc-search-surface="results"]');
    if (root) collect(root, P.initialQuery || new URL(location.href).searchParams.get('q') || '',
      'results', root.dataset.rcSearchCount, 'total_results');
  }
  function click(card, root, query, surface, count, scope) {
    const context = collect(root, query, surface, count, scope);
    if (!context) return;
    const details = {
      ...context, product_id: card.dataset.rcSearchProduct,
      product_type: card.dataset.rcSearchProductType || 'OTHER',
      result_position: Number(card.dataset.rcSearchPosition) || 1
    };
    if (send('rc_search_result_click', details) && details.query_status === 'safe') {
      const state = read() || { clicks: [] };
      // Attribute each SKU to its actual clicked search; later searches do not overwrite other SKUs.
      const clicks = (state.clicks || []).filter(c => c.product_id !== details.product_id && Date.now() - c.timestamp < ttl);
      clicks.push({ ...details, timestamp: Date.now() });
      write({ search_term: details.search_term, timestamp: Date.now(), clicks: clicks.slice(-10) });
    }
  }
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a');
    if (!link) return;
    // Native search links can contain the original term in _psq; keep it safe too.
    try {
      const url = new URL(link.href, location.origin);
      ['_psq', 'q'].forEach(k => { if (url.searchParams.has(k) && P.inspect(url.searchParams.get(k)).status !== 'safe') url.searchParams.delete(k); });
      if (url.origin === location.origin) link.href = url.href;
    } catch (_) {}
    const card = link.closest('[data-rc-search-product]');
    if (!card) return;
    const page = card.closest('[data-rc-search-surface="results"]');
    if (page) click(card, page, P.initialQuery, 'results', page.dataset.rcSearchCount, 'total_results');
    else {
      const component = card.closest('predictive-search-component');
      const root = component?.querySelector('[data-rc-search-surface="predictive"]');
      if (root && component.dataset.rcSearchRenderedQuery === component.querySelector('input[name="q"]')?.value.trim())
        click(card, root, component.dataset.rcSearchRenderedQuery, 'predictive', root.dataset.rcSearchCount, root.dataset.rcSearchCountScope || 'returned_products');
    }
  }, true);
  // Predictive requests are not searches-per-keystroke: log only settled, displayed result sets (800ms).
  document.addEventListener('rc:search-rendered', event => {
    const component = event.target;
    clearTimeout(component.__rcSearchTimer);
    component.__rcSearchTimer = setTimeout(() => {
      const root = component.querySelector('[data-rc-search-surface="predictive"]');
      if (root && component.dataset.rcSearchRenderedQuery === component.querySelector('input[name="q"]')?.value.trim())
        collect(root, component.dataset.rcSearchRenderedQuery, 'predictive', root.dataset.rcSearchCount, root.dataset.rcSearchCountScope || 'returned_products');
    }, 800);
  });
  document.addEventListener('cart:update', event => {
    const data = event.detail?.data;
    // Only successful cart-add responses, never quantity changes, button clicks, errors or add-on-only SKUs.
    if (data?.source !== 'product-form-component' || data.didError || !Array.isArray(data.searchAddedItems)) return;
    const state = read();
    if (!state) return;
    data.searchAddedItems.forEach(item => {
      const clicked = state.clicks?.find(c => String(c.product_id) === String(item.productId) && Date.now() - c.timestamp < ttl);
      if (!clicked || !item.variantId || !(Number(item.quantity) > 0)) return;
      const { timestamp, ...params } = clicked;
      send('rc_search_add_to_cart', { ...params, variant_id: String(item.variantId),
        quantity: Number(item.quantity), seconds_since_click: Math.floor((Date.now() - timestamp) / 1000) });
    });
  });
  function updateConsent() {
    const allowed = permitted();
    if (!allowed) wipe();
    if (googleStarted) window.gtag('consent', 'update', { analytics_storage: allowed ? 'granted' : 'denied' });
    if (clarityStarted) window.clarity('consentv2', { analytics_Storage: allowed ? 'granted' : 'denied', ad_Storage: 'denied' });
    if (allowed && !googleStarted) {
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
      window.gtag('consent', 'default', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
      window.gtag('js', new Date());
      window.gtag('config', 'G-3CJQ586K29', {
        page_location: P.safeUrl(location.href), page_referrer: P.safeUrl(document.referrer),
        page_title: /\/search$/.test(location.pathname) ? 'Search results | ReelCute' : document.title
      });
      const script = document.createElement('script'); script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-3CJQ586K29'; document.head.append(script);
      googleStarted = true;
    }
    if (allowed && !clarityStarted) {
      window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
      window.clarity('consentv2', { analytics_Storage: 'granted', ad_Storage: 'denied' });
      const script = document.createElement('script'); script.async = true;
      script.src = 'https://www.clarity.ms/tag/x8t6e94xyg'; document.head.append(script); clarityStarted = true;
    }
    if (allowed && !lastConsent) collectPage();
    lastConsent = allowed;
  }
  document.addEventListener('visitorConsentCollected', updateConsent);
  function initialize() {
    if (/\/search$/.test(location.pathname) && P.inspect(P.initialQuery).status === 'redacted')
      document.title = 'Search results | ReelCute';
    // Leave input masking enabled; also mask the query heading, never unmask arbitrary text.
    document.querySelectorAll('input[name="q"], .search-page__header').forEach(el => el.setAttribute('data-clarity-mask', 'true'));
    // Filters and infinite scrolling can replace result cards without a page reload.
    if (!P.preview && typeof MutationObserver === 'function' && document.querySelector('[data-rc-search-surface="results"]')) {
      let changedResults;
      new MutationObserver(() => {
        clearTimeout(changedResults);
        changedResults = setTimeout(collectPage, 150);
      }).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
    }
    if (window.Shopify?.customerPrivacy) updateConsent();
    else window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) updateConsent(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
